// 018 — Pre Anestésico (Anexo 1 MSP, SNS-MSP/HCU-form. 018/2021). Transcripción campo por campo del PDF oficial (páginas
// 81-82; spec en docs/hce-anexos/018-pre-anestesico.md), bloques A-J (la letra F está duplicada en el papel: "Escalas e
// índices" y "Tiempo de última ingesta"). El bloque A lo resuelve `EncabezadoMspCampos`; el resto está aquí.
//
// Equivalencia funcional, no facsímil. Decisiones:
// - E (laboratorio y gabinete): 42 rótulos + 4 de gabinete, todos texto libre — el papel no imprime unidades ni valores de
//   referencia ni dice qué es numérico. Las 4 filas sin rótulo (Ionograma y Hormonas) son sobrantes de la retícula: no son campos.
// - D (vía aérea): el papel ofrece rangos en casillas; cada grupo es una lista cerrada de una sola marca (aunque el instructivo
//   pide "registrar los valores", se sigue el papel).
// - B: "Electiva/Emergencia/Urgencia" y "Riesgo quirúrgico" son dos listas de una sola marca (el papel no lo dice, pero no
//   tiene sentido marcar dos).
// - J: un solo profesional (una fila, como el papel) y NO se teclea: fecha, hora, nombres, apellidos y número de documento los arma el
//   servidor al firmar (nombre del firmante de la sesión + cédula del panel de firma; `TIPOS_CON_CEDULA`). FIRMA y SELLO tampoco son campos.
// - FHIR (solo lo que tiene consumidor): una `Condition` por diagnóstico (sin `verificationStatus`, el papel no tiene PRE/DEF),
//   una `AllergyIntolerance` si "Alérgicos" trae algo distinto de una negativa ("ninguna", "no refiere"...) y los `Observation`
//   de signos vitales que se lean como número (TA, FC, FR, T°, SAT O2, peso, talla). Todo lo demás va como `Par[]` en el PDF.
import type { AllergyIntolerance, Condition, Encounter, Observation, Patient, Reference } from '@medplum/fhirtypes';
import { CLINICAL_STATUS_ALERGIA_ACTIVA, esRespuestaAlergiaNegativa } from '../extractors';
import {
  conCodigoCie,
  OPCIONES_SI_NO,
  paresDeCampos,
  valoresVacios,
  type CampoSimple,
  type OpcionCampo,
  type Valores,
} from './camposSimplesDatos';
import { codeDeDiagnostico, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { observacionesDeToma } from './signosVitalesToma';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';

export const NUM_DIAGNOSTICOS_018 = 2;

const opciones = (...etiquetas: string[]): OpcionCampo[] => etiquetas.map((e) => ({ valor: e, etiqueta: e }));
const texto = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
const lista = (clave: string, etiqueta: string, ...o: string[]): CampoSimple => ({ clave, etiqueta, tipo: 'select', opciones: opciones(...o) });

// ---------- B. Registro de valoración preanestésica ----------

export const CAMPOS_VALORACION_018: readonly CampoSimple[] = [
  area('procedimientoPropuesto', 'Procedimiento/s propuesto/s'),
  {
    clave: 'tipoCirugia',
    etiqueta: 'Tipo de cirugía',
    tipo: 'select',
    opciones: [
      { valor: 'electiva', etiqueta: 'Electiva' },
      { valor: 'emergencia', etiqueta: 'Emergencia' },
      { valor: 'urgencia', etiqueta: 'Urgencia' },
    ],
  },
  {
    clave: 'riesgoQuirurgico',
    etiqueta: 'Riesgo quirúrgico',
    tipo: 'select',
    opciones: [
      { valor: 'bajo', etiqueta: 'Bajo' },
      { valor: 'moderado', etiqueta: 'Moderado' },
      { valor: 'alto', etiqueta: 'Alto' },
    ],
  },
];

// ---------- C. Anamnesis ----------

export const FILAS_PATOLOGICOS_018: readonly FilaTablaConColumnas[] = Array.from({ length: 10 }, (_, i) => ({ clave: `p${i + 1}`, etiqueta: `${i + 1}.` }));
export const COLUMNAS_PATOLOGICOS_018: readonly ColumnaTablaConColumnas[] = [
  { clave: 'diagnostico', etiqueta: 'Diagnósticos' },
  { clave: 'evolucion', etiqueta: 'Tiempo de evolución' },
  { clave: 'tratamiento', etiqueta: 'Tratamiento' },
];

export const CAMPOS_ANTECEDENTES_018: readonly CampoSimple[] = [
  area('anestesicos', 'Anestésicos'),
  area('quirurgicos', 'Quirúrgicos'),
  area('alergicos', 'Alérgicos'),
  area('transfusiones', 'Transfusiones'),
  area('habitos', 'Hábitos'),
  area('familiares', 'Antecedentes patológicos familiares'),
];

// ---------- D. Examen físico ----------

export const CAMPOS_CONSTANTES_018: readonly CampoSimple[] = [
  texto('ta', 'TA'),
  texto('fc', 'FC'),
  texto('fr', 'FR'),
  texto('temperatura', 'T°'),
  texto('spo2', 'SAT O2'),
  texto('glasgow', 'Glasgow'),
];

export const CAMPOS_ANTROPOMETRIA_018: readonly CampoSimple[] = [
  { clave: 'peso', etiqueta: 'Peso (kg)', tipo: 'numero' },
  { clave: 'talla', etiqueta: 'Talla (cm)', tipo: 'numero' },
  { clave: 'imc', etiqueta: 'IMC (kg/m²)', tipo: 'numero' },
];

export const CAMPOS_VIA_AEREA_018: readonly CampoSimple[] = [
  lista('aperturaBucal', 'Apertura bucal (cm)', '<2', '2 - 2,5', '2,6 - 3', '>3'),
  lista('distanciaTiromentoniana', 'Distancia tiromentoniana (cm)', '<6', '6 - 6,5', '>6,5'),
  lista('mallampati', 'Mallampati', 'I', 'II', 'III', 'IV'),
  lista('protrusionMandibular', 'Protrusión mandibular', '<0', '0', '>0'),
  lista('perimetroCervical', 'Perímetro cervical (cm)', '<40', '>40'),
  lista('movilidadCervical', 'Movilidad cervical (°)', '<35', '>35'),
  { clave: 'intubacionDificil', etiqueta: 'Historia de intubación difícil', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'patologiaAsociada', etiqueta: 'Patología asociada a intubación difícil', tipo: 'select', opciones: OPCIONES_SI_NO },
  texto('otros', 'Otros'),
];

export const CAMPOS_REGIONES_018: readonly CampoSimple[] = [
  texto('torax', 'Tórax'),
  texto('corazon', 'Corazón'),
  texto('pulmones', 'Pulmones'),
  texto('abdomen', 'Abdomen'),
  texto('extremidades', 'Extremidades'),
  texto('sistemaNervioso', 'Sistema nervioso central'),
  texto('mets', 'Equivalente metabólico (METS)'),
];

// ---------- E. Resultados de exámenes de laboratorio, gabinete e imagen ----------

export interface GrupoLaboratorio018 {
  readonly clave: string;
  readonly titulo: string;
  readonly campos: readonly CampoSimple[];
}

const items = (grupo: string, ...rotulos: string[]): CampoSimple[] => rotulos.map((r) => texto(`${grupo}_${r.toLowerCase().replace(/[^a-z0-9]+/g, '')}`, r));

export const GRUPOS_LABORATORIO_018: readonly GrupoLaboratorio018[] = [
  { clave: 'hemograma', titulo: 'Hemograma', campos: items('hemograma', 'HCTO', 'HB', 'PLAQUETAS', 'TP', 'TTP', 'INR', 'LEUCOCITOS') },
  { clave: 'tipificacion', titulo: 'Tipificación', campos: items('tipificacion', 'GRUPO', 'FACTOR') },
  { clave: 'quimica', titulo: 'Química sanguínea', campos: items('quimica', 'GLUCOSA', 'UREA', 'CREATININA', 'OTROS') },
  { clave: 'hepatico', titulo: 'Perfil hepático', campos: items('hepatico', 'AST', 'ALT', 'FA', 'LDH', 'BT', 'BD', 'BI') },
  { clave: 'ionograma', titulo: 'Ionograma', campos: items('ionograma', 'Na', 'K', 'Cl', 'Ca', 'Mg') },
  { clave: 'gasometria', titulo: 'Gasometría', campos: items('gasometria', 'pH', 'PO2', 'PCO2', 'HCO3', 'EB', 'SAT O2', 'LACTATO') },
  {
    clave: 'hormonas',
    titulo: 'Hormonas',
    campos: [...items('hormonas', 'T4', 'TSH'), { clave: 'hormonas_pruebaembarazo', etiqueta: 'Prueba de embarazo', tipo: 'select', opciones: OPCIONES_SI_NO }],
  },
  { clave: 'orina', titulo: 'Orina', campos: items('orina', 'pH', 'DENSIDAD', 'BACTERIAS', 'LEUCOCITOS', 'PIOCITOS', 'HEMATÍES', 'GLUCOSA') },
];

export const CAMPOS_GABINETE_018: readonly CampoSimple[] = [texto('ekg', 'EKG'), texto('rxTorax', 'RX tórax'), texto('espirometria', 'Espirometría'), area('otros', 'Otros')];

// ---------- F. Escalas e índices / Tiempo de última ingesta ----------

export const CAMPOS_ESCALAS_018: readonly CampoSimple[] = [
  lista('asa', 'Estado físico ASA', 'I', 'II', 'III', 'IV', 'V', 'VI'),
  texto('riesgoCardiaco', 'Riesgo cardíaco'),
  texto('riesgoPulmonar', 'Riesgo pulmonar'),
  texto('riesgoTromboembolico', 'Riesgo tromboembólico'),
  texto('otros', 'Otros'),
];

export const CAMPOS_INGESTA_018: readonly CampoSimple[] = [
  texto('liquidosClaros', 'Líquidos claros'),
  texto('lecheFormula', 'Leche de fórmula'),
  texto('lecheMaterna', 'Leche materna'),
  texto('solidos', 'Sólidos'),
];

// ---------- G, H, I. Indicaciones, plan anestésico y observaciones ----------

export const CAMPOS_INDICACIONES_018: readonly CampoSimple[] = Array.from({ length: 8 }, (_, i) => texto(`i${i + 1}`, `${i + 1}.`));
export const CAMPOS_PLAN_018: readonly CampoSimple[] = [area('planAnestesico', 'Plan anestésico'), area('observaciones', 'Observaciones')];

// ---------- Estado ----------

export interface Estado018 {
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly valoracion: Valores;
  readonly patologicos: EstadoTablaConColumnas;
  readonly antecedentes: Valores;
  readonly constantes: Valores;
  readonly antropometria: Valores;
  readonly viaAerea: Valores;
  readonly regiones: Valores;
  readonly laboratorio: Valores;
  readonly gabinete: Valores;
  readonly escalas: Valores;
  readonly ingesta: Valores;
  readonly indicaciones: Valores;
  readonly plan: Valores;
}

const todosLosCamposDeLaboratorio = (): CampoSimple[] => GRUPOS_LABORATORIO_018.flatMap((g) => [...g.campos]);

export function estadoInicial018(): Estado018 {
  return {
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_018 }, () => ({ texto: '', cie10: null })),
    valoracion: valoresVacios(CAMPOS_VALORACION_018),
    patologicos: estadoInicialTablaConColumnas(FILAS_PATOLOGICOS_018, COLUMNAS_PATOLOGICOS_018),
    antecedentes: valoresVacios(CAMPOS_ANTECEDENTES_018),
    constantes: valoresVacios(CAMPOS_CONSTANTES_018),
    antropometria: valoresVacios(CAMPOS_ANTROPOMETRIA_018),
    viaAerea: valoresVacios(CAMPOS_VIA_AEREA_018),
    regiones: valoresVacios(CAMPOS_REGIONES_018),
    laboratorio: valoresVacios(todosLosCamposDeLaboratorio()),
    gabinete: valoresVacios(CAMPOS_GABINETE_018),
    escalas: valoresVacios(CAMPOS_ESCALAS_018),
    ingesta: valoresVacios(CAMPOS_INGESTA_018),
    indicaciones: valoresVacios(CAMPOS_INDICACIONES_018),
    plan: valoresVacios(CAMPOS_PLAN_018),
  };
}

// ---------- FHIR ----------

export function diagnosticosConTexto018(e: Estado018): DiagnosticoCie10Valor[] {
  return e.diagnosticos.filter((d) => d.texto.trim() !== '');
}

export function conditionsDe018(e: Estado018, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Condition[] {
  return diagnosticosConTexto018(e).map((d) => ({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(d), recordedDate: ahoraIso }));
}

/** `AllergyIntolerance` de "Alérgicos" — nada si está vacío o es una negativa ("ninguna", "no refiere"...). */
export function alergiaDe018(e: Estado018, subject: Reference<Patient>, ahoraIso: string): AllergyIntolerance | undefined {
  const texto = (e.antecedentes.alergicos ?? '').trim();
  if (texto === '' || esRespuestaAlergiaNegativa(texto)) return undefined;
  return { resourceType: 'AllergyIntolerance', patient: subject, clinicalStatus: CLINICAL_STATUS_ALERGIA_ACTIVA, code: { text: texto }, recordedDate: ahoraIso };
}

/** Observations de signos vitales: solo lo que se lee como número (TA "120/80", FC, FR, T°, SAT O2, peso, talla). */
export function observacionesDe018(e: Estado018, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahoraIso: string): Observation[] {
  return observacionesDeToma(
    { temperatura: e.constantes.temperatura, fc: e.constantes.fc, fr: e.constantes.fr, spo2: e.constantes.spo2, presion: e.constantes.ta, peso: e.antropometria.peso, talla: e.antropometria.talla },
    subject,
    encounter,
    ahoraIso,
  );
}

// ---------- PDF ----------

/** Serializa el formulario a `Par[]` para `onGuardado` (PDF firmado) — filtra respuestas vacías. */
export function paresDe018(t: Traducir, e: Estado018): Par[] {
  const p = (clave: string, campos: readonly CampoSimple[], valores: Valores): Par[] => paresDeCampos(campos, valores, `${t(`formularios.f018.${clave}`)} — `);
  const pares: Par[] = [
    ...e.diagnosticos.map((d, i) => ({ pregunta: `${t('formularios.f018.diagnosticoPregunta')} ${i + 1}`, respuesta: conCodigoCie(d) })),
    ...paresDeCampos(CAMPOS_VALORACION_018, e.valoracion),
    ...FILAS_PATOLOGICOS_018.map((f) => ({ pregunta: `${t('formularios.f018.patologicosTitulo')} ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.patologicos, f.clave, COLUMNAS_PATOLOGICOS_018) })),
    ...paresDeCampos(CAMPOS_ANTECEDENTES_018, e.antecedentes),
    ...p('constantesTitulo', CAMPOS_CONSTANTES_018, e.constantes),
    ...p('antropometriaTitulo', CAMPOS_ANTROPOMETRIA_018, e.antropometria),
    ...p('viaAereaTitulo', CAMPOS_VIA_AEREA_018, e.viaAerea),
    ...paresDeCampos(CAMPOS_REGIONES_018, e.regiones),
    ...GRUPOS_LABORATORIO_018.flatMap((g) => paresDeCampos(g.campos, e.laboratorio, `${g.titulo} — `)),
    ...p('gabineteTitulo', CAMPOS_GABINETE_018, e.gabinete),
    ...p('escalasTitulo', CAMPOS_ESCALAS_018, e.escalas),
    ...p('ingestaTitulo', CAMPOS_INGESTA_018, e.ingesta),
    ...p('indicacionesTitulo', CAMPOS_INDICACIONES_018, e.indicaciones),
    ...paresDeCampos(CAMPOS_PLAN_018, e.plan),
  ];
  return pares.filter((x) => x.respuesta.trim() !== '');
}
